"use client"

import { useState, useEffect, useCallback, useRef } from "react"
import { Bell, CheckCheck } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
} from "@/src/components/ui/dropdown-menu"
import { NotificationItemView, type NotificationData } from "./notification-item"
import {
  fetchNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from "@/lib/community/community-api"

/**
 * NotificationCenter — 通知铃铛 + 下拉面板
 *
 * 从后端 API 拉取通知列表。
 * 点击通知时根据 type + targetType 决定导航目标：
 * - like/favorite (targetType=post) → 帖子详情
 * - like/favorite (targetType=comment) → 该评论所在帖子（TODO: 后端需返回帖子 ID）
 * - comment/reply → 帖子详情（TODO: 同上）
 * - follow → 用户主页
 * - system → 仅标记已读
 */
export function NotificationCenter() {
  const [notifications, setNotifications] = useState<NotificationData[]>([])
  const [loading, setLoading] = useState(false)
  const [open, setOpen] = useState(false)

  const unreadCount = notifications.filter((n) => !n.isRead).length

  /* ── 拉取通知列表 ── */
  const loadNotifications = useCallback(async () => {
    setLoading(true)
    try {
      const resp = await fetchNotifications(1, 20)
      setNotifications(resp.list)
    } catch {
      // API 还不可用时静默失败
    } finally {
      setLoading(false)
    }
  }, [])

  /* ── 首次挂载 + 每次打开下拉时刷新 ── */
  useEffect(() => {
    loadNotifications()
  }, [loadNotifications])

  useEffect(() => {
    if (open) loadNotifications()
  }, [open, loadNotifications])

  /* ── 后台轮询：每 30 秒刷新 ── */
  const openRef = useRef(open)
  openRef.current = open

  useEffect(() => {
    const timer = setInterval(() => {
      if (!openRef.current) loadNotifications()
    }, 30_000)
    return () => clearInterval(timer)
  }, [loadNotifications])

  const handleMarkAllRead = useCallback(async () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, isRead: true })))
    try {
      await markAllNotificationsRead()
    } catch {
      // 静默失败
    }
  }, [])

  const handleClickNotification = useCallback(
    async (n: NotificationData) => {
      // 标记已读
      if (!n.isRead) {
        setNotifications((prev) =>
          prev.map((item) => (item.id === n.id ? { ...item, isRead: true } : item))
        )
        markNotificationRead(n.id).catch(() => {})
      }

      // 关闭下拉
      setOpen(false)

      // 导航
      switch (n.type) {
        case "like":
        case "favorite":
        case "comment":
        case "reply":
        case "mention":
          // targetType=post → 直接跳帖子
          // targetType=comment → 暂时也跳 targetId（后续后端可扩展返回 postId）
          if (n.targetId) {
            window.open(`${import.meta.env.BASE_URL}community/post/${n.targetId}`, "_blank")
          }
          break
        case "follow":
          if (n.senderId) {
            window.open(`${import.meta.env.BASE_URL}community/user/${n.senderId}`, "_blank")
          }
          break
        case "system":
        default:
          break
      }
    },
    []
  )

  return (
    <DropdownMenu open={open} onOpenChange={(details) => setOpen(details.open)}>
      <DropdownMenuTrigger asChild>
        <button
          type="button"
          className="relative flex h-8 w-8 items-center justify-center rounded-full text-muted-foreground hover:text-foreground hover:bg-secondary/60 transition-colors outline-none focus-visible:ring-2 focus-visible:ring-ring/50"
          aria-label="通知"
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-micro font-bold text-primary-foreground leading-none">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        side="bottom"
        align="end"
        sideOffset={14}
        collisionPadding={16}
        className="liquid-glass-panel rounded-xl border-0 w-80 p-0 z-[51]"
      >
        {/* 头部 */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border/20">
          <h3 className="text-sm font-semibold text-foreground">通知</h3>
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={handleMarkAllRead}
              className="flex items-center gap-1 text-caption text-primary hover:text-primary/80 transition-colors"
            >
              <CheckCheck className="h-3 w-3" />
              全部已读
            </button>
          )}
        </div>

        {/* 通知列表 */}
        <div className="overflow-y-auto max-h-[min(55vh,380px)] py-1">
          {loading && notifications.length === 0 ? (
            <div className="space-y-2 px-3 py-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-start gap-3 animate-pulse">
                  <div className="h-7 w-7 rounded-full bg-foreground/10 shrink-0" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 w-24 rounded bg-foreground/10" />
                    <div className="h-2.5 w-full rounded bg-foreground/10" />
                  </div>
                </div>
              ))}
            </div>
          ) : notifications.length > 0 ? (
            notifications.map((n) => (
              <NotificationItemView
                key={n.id}
                notification={n}
                onClick={handleClickNotification}
              />
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-10 text-muted-foreground">
              <Bell className="h-8 w-8 opacity-20 mb-2" />
              <p className="text-xs">暂无通知</p>
            </div>
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
